import React, { useEffect, useState } from 'react';
import styles from './GeneratePanel.module.css';
import { apiFetch } from '../api/client';
import { supabase } from '../auth/supabase';

export default function GeneratePanel({ patientId }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [debug, setDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const t = await apiFetch('/templates', {}, getToken);
        setTemplates(t);
      } catch (e) {
        setStatus(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generate = async () => {
    if (!selectedTpl) return;
    setStatus('Generating…');
    setDebugInfo(null);
    const path = (selectedTpl.file_path || '').toLowerCase();
    const isDocx = path.endsWith('.docx');
    try {
      if (!isDocx) {
        // PPTX flow
        const res = await apiFetch('/presentations/generate', { method: 'POST', body: { patientId, templateId: selectedTpl.id, debug } }, getToken);
        setStatus('Done. Your download should start shortly.');
        try { window.open(res.url, '_blank'); } catch (e) { /* ignore */ }
        if (res.debug) {
          setDebugInfo(res.debug);
          // eslint-disable-next-line no-console
          console.log('Presentation debug:', res.debug);
        }
      } else {
        // DOCX flow
        const token = await getToken();
        const url = `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/cases/${patientId}/docx/tx-plan?template=${encodeURIComponent(selectedTpl.file_path)}`;
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) {
          let msg = 'Failed to generate';
          try { const j = await res.json(); msg = j.message || msg; } catch (_) {}
          throw new Error(msg);
        }
        const blob = await res.blob();
        const cd = res.headers.get('content-disposition') || '';
        let filename = `tx-plan-${patientId}.docx`;
        const m = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
        const fn = decodeURIComponent((m && (m[1] || m[2])) || '');
        if (fn) filename = fn;
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        setStatus('Done. Your download should start shortly.');
      }
    } catch (e) {
      setStatus(e.message || 'Failed to generate');
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.title}>Select Template</div>
      <div className={styles.subtitle}>Choose a presentation template stored in Supabase Storage.</div>
      {loading ? (
        <div className={styles.info}>Loading…</div>
      ) : (
        <div className={styles.list}>
          {templates.map((t) => (
            <label key={t.id} className={styles.row}>
              <div className={styles.rowText}>
                <div className={styles.rowTitle}>{t.title}</div>
                <div className={styles.rowDesc}>{t.description || t.file_path}</div>
              </div>
              <input type="radio" name="tpl" onChange={() => { setSelected(t.id); setSelectedTpl(t); }} />
            </label>
          ))}
        </div>
      )}
      <div className={styles.footer}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={debug} onChange={(e) => setDebug(e.target.checked)} disabled={!!(selectedTpl && (selectedTpl.file_path || '').toLowerCase().endsWith('.docx'))} /> Debug
        </label>
        <button className={styles.generate} disabled={!selectedTpl} onClick={generate}>Generate</button>
        {status && <div className={styles.status}>{status}</div>}
      </div>
      {debugInfo && (
        <div className={styles.list} style={{ marginTop: 12 }}>
          <div className={styles.rowTitle}>Debug Summary</div>
          <div className={styles.rowDesc}>Shapes processed: {debugInfo.shapes?.length || 0}</div>
          <div className={styles.rowDesc}>
            Unmatched image shapes:
            {(() => {
              const arr = (debugInfo.shapes || []).filter(s => s.name && s.image_attempted === false);
              if (arr.length === 0) return ' none';
              return ' ' + arr.slice(0, 10).map(s => s.name).join(', ') + (arr.length > 10 ? '…' : '');
            })()}
          </div>
          <div className={styles.rowDesc}>
            Failed insertions:
            {(() => {
              const arr = (debugInfo.shapes || []).filter(s => s.image_attempted && !s.image_success);
              if (arr.length === 0) return ' none';
              return (
                <ul style={{ margin: '6px 0 0 16px' }}>
                  {arr.slice(0, 10).map((s, i) => (
                    <li key={i}>{s.name} → {s.matched_key || 'n/a'}: {s.error || 'unknown error'}</li>
                  ))}
                </ul>
              );
            })()}
          </div>
          <div className={styles.rowDesc}>
            Tip: open the browser console to see full debug details.
          </div>
        </div>
      )}
    </div>
  );
}
